import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { apiFetch } from '../../utils/apiInterceptor';
import {API_BASE_URL} from '../../utils/paths';

// Async thunks for API calls
export const loginUser = createAsyncThunk(
  'auth/login',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const loginWithGoogle = createAsyncThunk(
  'auth/google',
  async (credential, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const resendOtp = createAsyncThunk(
  'auth/resend-otp',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const verifyUser = createAsyncThunk(
  'auth/verify-email',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await fetch(`${API_BASE_URL}/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/logout`, {
        method: 'POST',
        body: {},
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      // Still remove token on error
      Cookies.remove('access_token');
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'auth/deleteAccount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/profile`, {
        method: 'DELETE',
        body: {},
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: Cookies.get('access_token') || null,
    isLoading: false,
    error: null,
    errors: {},
    isRegistering: false,
    isResendingOtp: false,
    successMessage: null,
    isVerifying: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearErrors: (state) => {
      state.errors = {};
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearCredentials: (state) => {
      state.user = null;
      state.token = null;
      Cookies.remove('access_token');
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.access_token || null;
        state.error = null;
        state.errors = {};
        state.successMessage = null;

        if(action.payload.access_token){
          Cookies.set('access_token', action.payload.access_token, { expires: 7 });
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Login failed';
        state.errors = action.payload?.errors || {};
        state.successMessage = null;

        // Backend returns the user (unverified email, 403) so Verify.js can redirect properly
        if (action.payload?.user) {
          state.user = action.payload.user;
        }
      })
      // Google Sign-In
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(loginWithGoogle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.access_token || null;
        state.error = null;
        state.errors = {};
        state.successMessage = null;

        if (action.payload.access_token) {
          Cookies.set('access_token', action.payload.access_token, { expires: 7 });
        }
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Google sign-in failed';
        state.errors = action.payload?.errors || {};
        state.successMessage = null;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.isRegistering = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isRegistering = false;
        state.user = action.payload.user;
        state.error = null;
        state.errors = {};
        state.successMessage = 'success';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isRegistering = false;
        state.error = action.payload?.message || 'Registration failed';
        state.errors = action.payload?.errors || {};
        state.successMessage = null;
      })
      // Resend OTP
      .addCase(resendOtp.pending, (state) => {
        state.isResendingOtp = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.isResendingOtp = false;
        state.error = null;
        state.errors = {};
        state.successMessage = 'We have resent the OTP to your email.';
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.isResendingOtp = false;
        state.error = action.payload?.message || 'Resend OTP failed';
        state.errors = action.payload?.errors || {};
        state.successMessage = null;
      })
      // Verify
      .addCase(verifyUser.pending, (state) => {
        state.isVerifying = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.isVerifying = false;
        state.user = action.payload.user;
        state.token = action.payload.access_token;
        state.error = null;
        state.errors = {};
        state.successMessage = 'Email verified successfully!';
        Cookies.set('access_token', action.payload.access_token, { expires: 7 });
      })
      .addCase(verifyUser.rejected, (state, action) => {
        state.isVerifying = false;
        state.error = action.payload?.message || 'Verification failed';
        state.errors = action.payload?.errors || {};
        state.successMessage = null;
      })
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.error = null;
        state.isLoading = false;
        state.errors = {};
        state.successMessage = null;
        Cookies.remove('access_token');
      })
      .addCase(logoutUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.error = null;
        state.isLoading = false;
        state.errors = {};
        state.successMessage = null;
        Cookies.remove('access_token');
      })
      // Update Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.error = null;
        state.errors = {};
        state.successMessage = 'Profile updated successfully!';
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Profile update failed';
        state.errors = action.payload?.errors || {};
        state.successMessage = null;
      })
      .addCase(deleteAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errors = {};
        state.successMessage = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.error = null;
        state.isLoading = false;
        state.errors = {};
        state.successMessage = null;
        Cookies.remove('access_token');
      })
      .addCase(deleteAccount.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.error = null;
        state.isLoading = false;
        state.errors = {};
        state.successMessage = null;
      });
  },
});

export const { clearError, clearErrors, setCredentials, clearCredentials, clearSuccessMessage } = authSlice.actions;
export default authSlice.reducer;
